import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Select,
  VStack,
  FormControl,
  FormLabel,
  Input,
  useToast,
  Text,
  HStack,
  Divider,
  Box,
  Icon,
} from "@chakra-ui/react";
import { User } from "../../../models/user";
import { renderLicenseIcon, renderLicenseIconbyId, userRoles } from "./UserRoleEnum";
import { updateUserLicense } from "../../../services/AdminServices/Users";
import { useAppContext } from "../../../context/AppContext";
import { GetAllTenant } from "../../../services/AdminServices/TenantService";
import { TenantModel } from "../../../models/TenantModel";
import { FiCalendar } from "react-icons/fi";

interface UserLicenceManageProps {
  isManageLicenseOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  users: User[];
  selectedLicenseUser: User | null;
}

const UserLicenceManage: React.FC<UserLicenceManageProps> = ({
  isManageLicenseOpen,
  onClose,
  onSave,
  users,
  selectedLicenseUser,
}) => {
  const { accessToken, userData } = useAppContext();
  const toast = useToast();
  const [currentUser, setCurrentUser] = useState<User | null>(selectedLicenseUser);
  const [tenants, setTenants] = useState<TenantModel[]>([]);
  const tenantID = userData?.tenantID || 0;
  const [isLicenseChanged, setIsLicenseChanged] = useState(false);

  useEffect(() => {
    setCurrentUser(selectedLicenseUser);
  }, [selectedLicenseUser]);

  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const response = await GetAllTenant(accessToken, tenantID);
        setTenants(response);
      } catch (error) {
        console.error("Error fetching tenants:", error);
        toast({
          title: "Error loading licenses",
          description: "Failed to fetch license information",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    };
    fetchTenants();
  }, [accessToken, tenantID]);

  // Count active users for a license (not removed and license is active)
  const getLicenseUsage = (tenantLicenseID: number): number => {
    return users.filter(user => 
      user.tenantLicenseID === tenantLicenseID && 
      !user.isUserLicenseRemoved && 
      user.IsLicenseActive
    ).length;
  };

  // Get the maximum allowed users for a license
  const getMaxAllowedUsers = (tenantLicenseID: number): number => {
    const license = tenants.find(t => t.TenantLicenseID === tenantLicenseID);
    return license ? license.NoOfUsers : 0;
  };

  // Check if assigning this license would exceed capacity
  const wouldExceedLimit = (licenseId: number): boolean => {
    const usage = getLicenseUsage(licenseId);
    const limit = getMaxAllowedUsers(licenseId);
    return usage >= limit;
  };

  const handleLicenseChange = (value: string) => {
    setIsLicenseChanged(true)

    if (!currentUser) return;
    
    const newID = value === "" ? null : Number(value);
    const originalLicenseId = selectedLicenseUser?.tenantLicenseID;
    
    // Check if this is a new assignment to a different license
    if (newID !== null && newID !== originalLicenseId) {
      if (wouldExceedLimit(newID)) {
        toast({
          position: "top",
          title: "License Limit Reached",
          description: "No available slots in this license",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
    }

    const selectedLicense = newID ? tenants.find(t => t.TenantLicenseID === newID) : null;
    
    setCurrentUser(prev => ({
      ...prev!,
      tenantLicenseID: newID,
      licenseType: selectedLicense?.LicenseType ?? null,
      isUserLicenseRemoved: newID === null,
      IsLicenseActive: newID !== null
    }));
  };

  const handleSave = async () => {
    if (!currentUser) return;
    
    // Final validation before saving
    if (currentUser.tenantLicenseID !== null) {
      const originalUser = users.find(u => u.userID === currentUser.userID);
      const isNewAssignment = originalUser?.tenantLicenseID !== currentUser.tenantLicenseID;
      
      if (isNewAssignment && wouldExceedLimit(currentUser.tenantLicenseID)) {
        toast({
          position: "top",
          title: "License Full",
          description: "This license has no available slots",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
    }

    try {
      await updateUserLicense(currentUser, accessToken, tenantID);
      toast({
        title: "License updated",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      onSave();
      onClose();
    } catch (error) {
      console.error("Error updating user license:", error);
      toast({
        title: "Update failed",
        description: "Could not update user license",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Get license dates for display
  const selectedTenant = currentUser?.tenantLicenseID 
    ? tenants.find(t => t.TenantLicenseID === currentUser.tenantLicenseID)
    : null;

  const formatDate = (date: Date | string | null): string => {
    if (!date) return "-";
    const d = typeof date === "string" ? new Date(date) : date;
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <Modal isOpen={isManageLicenseOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent borderRadius="md">
        <ModalHeader
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Text fontSize="lg" fontWeight="bold">
            Manage User License
          </Text>
          <ModalCloseButton position="static" />
        </ModalHeader>
        <Divider />
        <ModalBody>
          {currentUser && (
            <VStack align="start" spacing={4} mt={4}>
              <Box>
                <Text fontWeight="bold" mb={1}>
                  Username
                </Text>
                <Text>{currentUser.userName}</Text>
              </Box>
              <Box>
                <Text fontWeight="bold" mb={1}>
                  Email
                </Text>
                <Text>{currentUser.Office365MailId}</Text>
              </Box>
              
              <FormControl>
                <FormLabel>License Type</FormLabel>
                <HStack>
                  <Select
                    value={currentUser.tenantLicenseID ?? ""}
                    onChange={(e) => handleLicenseChange(e.target.value)}
                  >
                    <option value="">Remove License</option>
                    {tenants.map(license => (
                      <option 
                        key={license.TenantLicenseID} 
                        value={license.TenantLicenseID}
                        disabled={wouldExceedLimit(license.TenantLicenseID) && 
                                 license.TenantLicenseID !== currentUser.tenantLicenseID}
                      >
                        {license.LicenceTypeName} - {license.Title}
                        {wouldExceedLimit(license.TenantLicenseID) && 
                         license.TenantLicenseID !== currentUser.tenantLicenseID && 
                         " (Full)"}
                      </option>
                    ))}
                  </Select>
                  {currentUser.licenseType !== null && (
                    renderLicenseIconbyId(currentUser.licenseType)
                  )}
                </HStack>
              </FormControl>

              <HStack spacing={8} align="flex-start">
                <VStack align="start" spacing={1}>
                  <Text fontWeight="bold">Start Date</Text>
                  <HStack>
                    <Icon as={FiCalendar} boxSize={4} color="gray.500" />
                    <Text>
                      {selectedTenant ? formatDate(selectedTenant.LicenseStartDate) : "-"}
                    </Text>
                  </HStack>
                </VStack>

                <VStack align="start" spacing={1}>
                  <Text fontWeight="bold">End Date</Text>
                  <HStack>
                    <Icon as={FiCalendar} boxSize={4} color="gray.500" />
                    <Text>
                      {selectedTenant ? formatDate(selectedTenant.LicenseEndDate) : "-"}
                    </Text>
                  </HStack>
                </VStack>
              </HStack>

              {currentUser.tenantLicenseID && (
                <Box pt={2}>
                  <Text fontSize="sm" color="gray.600">
                    {getLicenseUsage(currentUser.tenantLicenseID)} of {getMaxAllowedUsers(currentUser.tenantLicenseID)} slots used
                  </Text>
                </Box>
              )}
            </VStack>
          )}
        </ModalBody>
        <Divider />
        <ModalFooter>
          <Button colorScheme="blue" onClick={handleSave} mr={3} disabled={!isLicenseChanged}>
            Save Changes
          </Button>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default UserLicenceManage;